import SpreadReadingPage from './SpreadReadingPage';

const ORIGIN = 'https://nowtarot.com';

const config = {
  pickerPage: 'cardOfDay',
  path: '/card-of-the-day/',
  spreadId: 'dailyReading',
  cardCount: 1,
  longCardMeaning: true,
  alternates: [
    { hrefLang: 'x-default', href: `${ORIGIN}/card-of-the-day/` },
    { hrefLang: 'en', href: `${ORIGIN}/card-of-the-day/` },
    { hrefLang: 'es', href: `${ORIGIN}/es/carta-del-dia/` },
    { hrefLang: 'ar', href: `${ORIGIN}/ar/card-of-the-day/` },
  ],
  metaTitle: 'Tarot Card of the Day — Draw One Card Free | NowTarot AI',
  metaDescription:
    'Draw your tarot card of the day from the full 78-card deck. Read what that one card means for today, then come back tomorrow for a new pull. Free, no signup.',
  metaKeywords:
    'tarot card of the day, card of the day, free tarot card of the day, today tarot card, daily card pull, one card tarot',
  heading: 'Tarot Card of the Day',
  intro:
    'A tarot card of the day is one card you choose as today’s theme. Shuffle the full 78-card deck, pick the card that draws you, and read it as a single image for the day — not a forecast of every hour. The pull is yours. It is not the same card shown to every visitor.',
  howItWorks:
    'Think of one thing this day is about: a conversation, a piece of work, or simply how you want to meet the morning. Shuffle, then click one face-down card. The reading names the card and gives its meaning. For love, work, and money in more detail, open that card’s full meaning page.',
  contentSections: [
    {
      heading: 'What a card of the day is for',
      items: [
        {
          title: 'A theme, not a script',
          text: 'One card cannot schedule your afternoon. Treat it as a lens. If you draw The Star, look for where you need patience. If you draw a Pentacle, look at something practical you can actually finish.',
        },
        {
          title: 'One new card at a time',
          text: 'People use a card of the day to learn the deck without memorizing all 78 at once. The card you pick today is the one to sit with. Tomorrow’s pull can be a different card.',
        },
        {
          title: 'Your card, not a shared one',
          text: 'Nothing here assigns a single card to the whole internet. You shuffle and you choose. Two people opening this page on the same morning can leave with two different cards.',
        },
      ],
    },
    {
      heading: 'How to read the card you picked',
      items: [
        {
          title: 'Start with the picture',
          text: 'Before the keywords, notice one object in the art: a cup, a road, a closed door, an animal. Ask where something like that shows up in your actual day.',
        },
        {
          title: 'Then the suit or the major arcana',
          text: 'Wands lean toward action, Cups toward feeling, Swords toward a decision or a conversation, Pentacles toward work and money. A major arcana card is usually the louder theme of the day.',
        },
        {
          title: 'Keep it to one sentence',
          text: 'Write a single line you can remember at lunch, such as “finish the thing in front of me” or “don’t answer that message in a hurry.” That line is the reading.',
        },
      ],
    },
    {
      heading: 'Cards that often land as a card of the day',
      items: [
        {
          title: 'The Magician',
          text: 'You already have the tools for the thing you keep postponing. The card of the day is a prompt to use one of them before the day gets away from you.',
        },
        {
          title: 'Ace of Cups',
          text: 'A new feeling, a kinder reply, or a conversation you have been avoiding. It is a beginning, not a promise that everything is settled.',
        },
        {
          title: 'Ten of Pentacles',
          text: 'Home, money, or a long-running responsibility wants attention. Look at what you are building, not only what you want today.',
        },
      ],
    },
  ],
  faqHeading: 'Card of the Day FAQ',
  faq: [
    {
      question: 'What is a tarot card of the day?',
      answer:
        'It is a one-card pull used as today’s theme. You shuffle the deck, choose one card, and read that card’s meaning against the day in front of you.',
    },
    {
      question: 'Is the card of the day the same for everyone?',
      answer:
        'No. You pick your own card from the full deck. NowTarot does not publish one shared card for all visitors.',
    },
    {
      question: 'Can I draw a new card of the day tomorrow?',
      answer:
        'Yes. Come back and shuffle again. A card of the day is for that visit. Drawing again the next day is the habit. Drawing again immediately, hoping for a nicer card, throws the first one away.',
    },
    {
      question: 'Where is the longer meaning?',
      answer:
        'It appears in the reading after you pick: the card’s meaning, then love, career, and money. The link under that text opens the full card page.',
    },
  ],
  crossLinks: (
    <>
      Want a reading about how to use the card through the day? Try the{' '}
      <a href="/daily-tarot-reading/" style={{ color: '#FFD700' }}>daily tarot reading</a>
      . To study the deck, browse{' '}
      <a href="/tarot-card-meanings/" style={{ color: '#FFD700' }}>tarot card meanings</a>
      , or ask a{' '}
      <a href="/yes-or-no-tarot/" style={{ color: '#FFD700' }}>yes or no tarot</a> question.
    </>
  ),
};

const CardOfTheDayReading = () => <SpreadReadingPage config={config} />;

export default CardOfTheDayReading;
